import { useMemo, useState } from "react";
import { C, S } from "../customers/theme";
import { GRN_STATUSES } from "../lib/goodsReceived";
import { todayISO } from "../lib/dates";

const num = (n) => Number(n) || 0;
const round3 = (n) => Math.round((Number(n) || 0) * 1000) / 1000;
const remaining = (l) => Math.max(0, round3(num(l.ordered_quantity) - num(l.previously_received) - num(l.quantity_received)));

let _seq = 0;
function Field({ label, value, onChange, type = "text", options, placeholder }) {
  const id = useMemo(() => `gr-${++_seq}`, []);
  return (
    <div>
      <label htmlFor={id} style={S.label}>{label}</label>
      {options ? (
        <select id={id} style={S.input} value={value ?? ""} onChange={(e) => onChange(e.target.value)} disabled={options.disabled}>
          {options.map((o) => <option key={String(o.value)} value={o.value}>{o.label}</option>)}
        </select>
      ) : (
        <input id={id} style={S.input} type={type} value={value ?? ""} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} />
      )}
    </div>
  );
}

// Build receivable GRN lines from a purchase order's outstanding lines.
const linesFromPO = (po) =>
  (po?.purchase_order_lines || [])
    .map((pol) => {
      const ordered = num(pol.quantity), prev = num(pol.qty_received);
      const rem = Math.max(0, round3(ordered - prev));
      return {
        purchase_order_line_id: pol.id, product_id: pol.product_id || null, sku: pol.sku || "",
        description: pol.description || "", unit_cost: pol.unit_cost ?? null,
        ordered_quantity: ordered, previously_received: prev, quantity_received: rem,
        quantity_rejected: 0, rejection_reason: "", batch_number: "", expiry_date: "",
      };
    })
    .filter((l) => l.ordered_quantity > l.previously_received || l.quantity_received > 0);

const suggestStatus = (lines) => {
  if (!lines.length) return "pending";
  const anyRecv = lines.some((l) => num(l.quantity_received) > 0);
  const allDone = lines.every((l) => remaining(l) === 0);
  const anyRej = lines.some((l) => num(l.quantity_rejected) > 0);
  if (allDone && anyRecv) return "fully_received";
  if (anyRecv) return "partially_received";
  if (anyRej) return "rejected";
  return "pending";
};

/**
 * Create/Edit a Goods Received Note. Receives the receivable POs as a prop (loaded
 * by the manager via the data layer) — never touches Supabase directly.
 */
export default function GRNForm({ initial, receivablePOs = [], onCancel, onSave }) {
  const seed = initial || {};
  const isEdit = !!seed.id;
  const editPO = seed.purchase_orders; // embedded on edit
  const [f, setF] = useState({
    purchase_order_id: seed.purchase_order_id || "",
    supplier_id: seed.supplier_id || "",
    delivery_date: seed.delivery_date || todayISO(),
    delivery_reference: seed.delivery_reference || "",
    received_by: seed.received_by || "",
    status: seed.status || "pending",
    notes: seed.notes || "",
  });
  const [lines, setLines] = useState((seed.goods_received_note_lines || []).map((l) => ({ ...l })));
  const [touchedStatus, setTouchedStatus] = useState(isEdit);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v }));
  const selectedPO = receivablePOs.find((p) => p.id === f.purchase_order_id) || (isEdit ? editPO : null);
  const supplier = selectedPO?.suppliers || seed.suppliers;

  const applyStatus = (next) => setF((p) => ({ ...p, status: touchedStatus ? p.status : suggestStatus(next) }));

  const onPO = (id) => {
    const po = receivablePOs.find((p) => p.id === id);
    const newLines = linesFromPO(po);
    setF((p) => ({ ...p, purchase_order_id: id || "", supplier_id: po?.supplier_id || null }));
    setLines(newLines);
    if (!touchedStatus) setF((p) => ({ ...p, status: suggestStatus(newLines) }));
  };

  const updLine = (i, patch) => setLines((p) => { const next = p.map((l, j) => (j === i ? { ...l, ...patch } : l)); applyStatus(next); return next; });

  // delivery summary
  const sum = lines.reduce((a, l) => ({
    ordered: a.ordered + num(l.ordered_quantity), prev: a.prev + num(l.previously_received),
    received: a.received + num(l.quantity_received), rejected: a.rejected + num(l.quantity_rejected),
    remaining: a.remaining + remaining(l),
  }), { ordered: 0, prev: 0, received: 0, rejected: 0, remaining: 0 });

  const submit = async () => {
    setError("");
    if (!f.purchase_order_id) { setError("Please select a purchase order."); return; }
    if (!lines.length) { setError("This purchase order has nothing outstanding to receive."); return; }
    setBusy(true);
    try {
      await onSave({ ...f, supplier_id: f.supplier_id || null, lines: lines.map(({ id, quantity_remaining, position, ...rest }) => rest) });
    } catch (e) { setError(e.message || "Could not save goods received note"); setBusy(false); }
  };

  const cell = { ...S.input, padding: "6px 8px", fontSize: 12 };
  const ro = { ...cell, background: C.surface, color: C.textMuted };

  return (
    <div style={S.modal} onClick={(e) => e.target === e.currentTarget && onCancel()}>
      <div style={{ ...S.modalBox, width: 940 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>
            {isEdit ? "Edit Goods Received Note" : "New Goods Received Note"}
            {seed.grn_number && <span style={{ color: C.textMuted, fontWeight: 500, fontSize: 13, marginLeft: 8, fontFamily: "monospace" }}>{seed.grn_number}</span>}
          </h2>
          <button onClick={onCancel} aria-label="Close" style={{ background: "none", border: "none", fontSize: 20, cursor: "pointer", color: C.textMuted }}>✕</button>
        </div>

        <div style={S.grid2}>
          {isEdit ? (
            <div><label style={S.label}>Purchase Order</label><input style={{ ...S.input, background: C.surface, color: C.textMuted }} value={editPO?.po_number || "—"} readOnly /></div>
          ) : (
            <Field label="Purchase Order *" value={f.purchase_order_id} onChange={onPO}
              options={[{ value: "", label: receivablePOs.length ? "Select a purchase order…" : "No open purchase orders" }, ...receivablePOs.map((p) => ({ value: p.id, label: `${p.po_number} — ${p.suppliers?.company_name || ""}` }))]} />
          )}
          <Field label="Delivery Status" value={f.status} onChange={(v) => { setTouchedStatus(true); set("status")(v); }} options={GRN_STATUSES} />
          <Field label="Delivery Date" type="date" value={f.delivery_date} onChange={set("delivery_date")} />
          <Field label="Delivery Reference" value={f.delivery_reference} onChange={set("delivery_reference")} placeholder="Docket / waybill no." />
          <Field label="Received By" value={f.received_by} onChange={set("received_by")} />
        </div>

        {supplier && (
          <div style={{ marginTop: 12, background: C.surface2, border: `1px solid ${C.border}`, borderRadius: 8, padding: "10px 14px", fontSize: 12, color: C.textMuted, display: "flex", gap: 18, flexWrap: "wrap" }}>
            <span><strong style={{ color: C.text }}>{supplier.company_name}</strong></span>
            {supplier.contact_name && <span>👤 {supplier.contact_name}</span>}
            {supplier.email && <span>✉️ {supplier.email}</span>}
            {supplier.phone && <span>☎️ {supplier.phone}</span>}
          </div>
        )}

        <div style={S.sectionTitle}>Items received</div>
        <div style={{ overflowX: "auto" }}>
          <div style={{ minWidth: 820 }}>
            <div style={{ display: "grid", gridTemplateColumns: "1.6fr 90px 64px 64px 78px 74px 64px 110px 110px", gap: 6, marginBottom: 6, fontSize: 11, fontWeight: 700, color: C.textDim }}>
              {["Product", "SKU", "Ordered", "Prev", "Receive", "Remaining", "Reject", "Batch / Lot", "Expiry"].map((h) => <div key={h}>{h}</div>)}
            </div>
            {lines.length === 0 ? (
              <div style={{ ...S.empty, padding: "20px 0" }}>{isEdit ? "No lines." : "Select a purchase order to load outstanding items."}</div>
            ) : lines.map((l, i) => (
              <div key={i}>
                <div style={{ display: "grid", gridTemplateColumns: "1.6fr 90px 64px 64px 78px 74px 64px 110px 110px", gap: 6, marginBottom: 4, alignItems: "center" }}>
                  <input style={cell} value={l.description || ""} placeholder="Description" onChange={(e) => updLine(i, { description: e.target.value })} />
                  <input style={ro} value={l.sku || ""} readOnly />
                  <input style={ro} value={l.ordered_quantity} readOnly />
                  <input style={ro} value={l.previously_received} readOnly />
                  <input style={cell} type="number" value={l.quantity_received} onChange={(e) => updLine(i, { quantity_received: +e.target.value })} />
                  <input style={ro} value={remaining(l)} readOnly />
                  <input style={cell} type="number" value={l.quantity_rejected} onChange={(e) => updLine(i, { quantity_rejected: +e.target.value })} />
                  <input style={cell} value={l.batch_number || ""} placeholder="Batch" onChange={(e) => updLine(i, { batch_number: e.target.value })} />
                  <input style={cell} type="date" value={l.expiry_date || ""} onChange={(e) => updLine(i, { expiry_date: e.target.value })} />
                </div>
                {num(l.quantity_rejected) > 0 && (
                  <input style={{ ...cell, marginBottom: 6, width: "60%" }} value={l.rejection_reason || ""} placeholder="Rejection reason…" onChange={(e) => updLine(i, { rejection_reason: e.target.value })} />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Delivery summary panel */}
        {lines.length > 0 && (
          <div style={{ display: "flex", gap: 18, flexWrap: "wrap", background: C.surface2, borderRadius: 8, padding: "10px 16px", marginTop: 12, fontSize: 13 }}>
            <span style={{ color: C.textMuted }}>Ordered <strong style={{ color: C.text }}>{round3(sum.ordered)}</strong></span>
            <span style={{ color: C.textMuted }}>Receiving <strong style={{ color: C.success }}>{round3(sum.received)}</strong></span>
            <span style={{ color: C.textMuted }}>Rejected <strong style={{ color: sum.rejected > 0 ? C.danger : C.text }}>{round3(sum.rejected)}</strong></span>
            <span style={{ color: C.textMuted }}>Outstanding after <strong style={{ color: sum.remaining > 0 ? C.warning : C.success }}>{round3(sum.remaining)}</strong></span>
          </div>
        )}

        <div style={{ marginTop: 14 }}>
          <label style={S.label}>Notes</label>
          <textarea style={{ ...S.input, height: 56 }} value={f.notes} onChange={(e) => set("notes")(e.target.value)} />
        </div>

        {error && <div style={{ color: C.danger, fontSize: 13, fontWeight: 600, marginTop: 14 }} role="alert">{error}</div>}

        <div style={{ display: "flex", gap: 10, marginTop: 20, justifyContent: "flex-end" }}>
          <button type="button" style={S.btn("secondary")} onClick={onCancel} disabled={busy}>Cancel</button>
          <button type="button" style={{ ...S.btn(), opacity: busy ? 0.6 : 1 }} onClick={submit} disabled={busy}>
            {busy ? "Saving…" : isEdit ? "Save Changes" : "Receive Goods"}
          </button>
        </div>
      </div>
    </div>
  );
}
